import { createHealthRelay, type HealthStore } from '@joshua/supabase-guard';

/**
 * Web session health (JOSH-89): `reconnecting` from the moment the main
 * client's token refresh hits a 429 cooldown until a refresh succeeds (or the
 * user signs in or out), `ok` otherwise.
 *
 * The truth lives in the session guard bound to the main client; this module
 * only relays its store. It is kept apart from `@/lib/supabase` because many
 * test files mock that module, and a mocked client must not leave health
 * undefined: with no source attached, health is always `ok`.
 */

const relay = createHealthRelay();

/** Attaches the guard's health store. Called once by `@/lib/supabase`. */
export function setRefreshHealthSource(store: HealthStore | null): void {
  relay.connect(store);
}

/** Store shaped for `useSyncExternalStore`; snapshots are referentially stable. */
export const refreshHealth: HealthStore = relay.store;

export function isSessionReconnecting(): boolean {
  return refreshHealth.getSnapshot().status === 'reconnecting';
}

/**
 * Calls `listener` each time health moves from `reconnecting` to `ok`.
 * Returns the unsubscribe function.
 */
export const onHealthRecovered: (listener: () => void) => () => void = relay.onRecovered;
